import { createHash } from "crypto"

import { useEffect, useState } from "react"
import { useAtom } from "@reatom/npm-react"
import { useFeature } from "@growthbook/growthbook-react"
import { useLocation } from "react-router-dom"

import { ClientLoginForm } from "../ClientAuthorization/Default/ClientLoginForm"
import { isAuthByPromocodePage } from "../../app/AppRouter/utils"
import { useClientContext } from "../ClientAuthorization/common/ClientContext"
import { CLIENT_AUTHORIZATION_PLACE } from "../ClientAuthorization/Default/common/constant"

import styles from "./style.module.scss"
import { SurveyResultsStorage } from "./common/utils/SurveyResultsStorage"

import {
    PageType,
    SurveyQuestion,
    SurveyQuestions,
    useGetSurveyBySlug,
    useGetSurveyList,
} from "modules/NewClientSurvey/common"
import { PageWrapper } from "modules/NewClientSurvey/components/PageWrapper"
import { SurveyAnswersStorage } from "modules/NewClientSurvey/common/utils"
import { StepTwoQuestions } from "modules/NewClientSurvey/components/StepTwoQuestions"
import { StepOneList } from "modules/NewClientSurvey/components/StepOneList"
import { answersAtom, currentPageAtom, questionAtom } from "modules/NewClientSurvey/common/models"
import { Spinner } from "components/Spinner"
import { Modal } from "@components-branded/Modal"

export function NewClientSurvey() {
    const { state } = useLocation() as { state?: { slug?: string } }
    const dataStorage = SurveyAnswersStorage.getInstance().get()
    const { data: cards, isFetching: listIsFetching } = useGetSurveyList()
    const [slug, setSlug] = useState<string>(dataStorage?.slug || "")
    const [currentPage, setCurrentPage] = useAtom(currentPageAtom)
    const [, setAnswers] = useAtom(answersAtom)
    const [question, setQuestion] = useAtom(questionAtom)
    const [startSurvey, setStartSurvey] = useState<boolean>(false)
    const [{ isVerified }] = useClientContext()
    const { value: experimentClosedProduct } = useFeature<boolean>("closed-product")
    const [fromResult, toggleFromResult] = useState(() => !!state?.slug)

    const {
        data: surveyQuestions,
        isFetching: cardsIsFetching,
        isSuccess,
    } = useGetSurveyBySlug(slug)

    useEffect(() => {
        if (state?.slug) {
            goToQuestions(`survey/${state.slug}`)
        }
    }, [])

    useEffect(() => {
        if (fromResult && surveyQuestions) {
            const reportedProblemsIndex = surveyQuestions.questions.findIndex(
                (item) => item.ref === "reported_problems"
            )
            if (reportedProblemsIndex !== -1) {
                setAnswers((prev) => {
                    return Object.fromEntries(
                        Object.entries(prev).filter(
                            ([, answer]) => answer.sequence_number < reportedProblemsIndex
                        )
                    )
                })
                setQuestion(surveyQuestions.questions[reportedProblemsIndex])
            }
        }
    }, [surveyQuestions])

    useEffect(() => {
        if (startSurvey && surveyQuestions && isSuccess && !cardsIsFetching) {
            if (!fromResult) {
                createSurvey(slug, surveyQuestions)
            } else {
                toggleFromResult(false)
            }
            setStartSurvey(false)
            setCurrentPage(PageType.QUESTION_PAGE)
        }
    }, [surveyQuestions, isSuccess, cardsIsFetching, fromResult])

    const goToQuestions = (slug: string) => {
        cleanSurvey()
        if (!state?.slug) {
            setAnswers({})
        }
        setSlug(slug)
        setStartSurvey(true)
    }

    function previousPage() {
        setCurrentPage(PageType.CARDS_PAGE)
    }

    function handleClickCardItem(newSlug: string) {
        if (newSlug !== slug) {
            goToQuestions(newSlug)
        } else setCurrentPage(PageType.QUESTION_PAGE)
    }

    function cleanSurvey() {
        setQuestion({} as SurveyQuestion)
        setSlug("")
        setCurrentPage(PageType.CARDS_PAGE)
    }

    function createSurvey(slug: string, surveyQuestions: SurveyQuestions) {
        if (fromResult) {
            return
        }
        SurveyAnswersStorage.getInstance().clean()
        SurveyResultsStorage.clean()
        setAnswers({})

        const surveyQuestionsHash = createHash("md5")
            .update(JSON.stringify(surveyQuestions))
            .digest("hex")

        if (surveyQuestions) {
            SurveyAnswersStorage.getInstance().create(
                slug,
                surveyQuestions.questions[0],
                surveyQuestionsHash
            )
            setQuestion(surveyQuestions.questions[0])
        }
    }

    const loginModal = !isVerified && experimentClosedProduct && (
        <Modal isActive>
            <Modal.Content className={styles.modal} isFullHeightMobile={true}>
                <ClientLoginForm
                    byPromocode={isAuthByPromocodePage()}
                    place={CLIENT_AUTHORIZATION_PLACE.MODAL}
                    firstRegistration
                />
            </Modal.Content>
        </Modal>
    )

    if (listIsFetching || (fromResult && currentPage === PageType.CARDS_PAGE)) {
        return (
            <PageWrapper withSpinner>
                <Spinner fullscreen />
            </PageWrapper>
        )
    }

    if (currentPage === PageType.QUESTION_PAGE && !slug) {
        return (
            <PageWrapper withSpinner>
                <Spinner fullscreen text="&nbsp;&nbsp;&nbsp;Подбираем психологов..." />
            </PageWrapper>
        )
    }

    if ((currentPage === PageType.CARDS_PAGE || (cardsIsFetching && !question)) && cards) {
        return (
            <>
                {loginModal}
                <StepOneList cards={cards} slug={slug} handleClickCardItem={handleClickCardItem} />
            </>
        )
    }

    if (currentPage === PageType.QUESTION_PAGE && slug && surveyQuestions) {
        return (
            <>
                {loginModal}
                <StepTwoQuestions
                    prevPage={previousPage}
                    slug={slug}
                    surveyQuestions={surveyQuestions}
                    setSlug={setSlug}
                    cleanSurvey={cleanSurvey}
                />
            </>
        )
    }

    return null
}
